import type { Timestamp } from 'firebase/firestore';
import { isObject, mapValues } from 'remeda';

export type Replace<T, S, R> = {
  [K in keyof T]: NonNullable<T[K]> extends S ? R : T[K];
};

export type ReplaceRecursive<T, S, R> = [NonNullable<T>] extends [S]
  ? R
  : T extends object
    ? {
        [K in keyof T]: ReplaceRecursive<T[K], S, R>;
      }
    : T;

export interface ITimestamp {
  toDate(): Date;
  toMillis(): number;
}

export type TTimestampedSerialized<T> = Replace<T, ITimestamp, number>;

export type TTimestampedSerializedRecursive<T> = ReplaceRecursive<T, ITimestamp, number>;

/**
 * Generic utility type to make Timestamps nullable recursively.
 *
 * This is due to Firestore behaviour when setting a timestamp with `serverTimestamp` the client
 * returns null for it before getting the server response.
 */
export type WithNullableTimestamps<T> = T extends Timestamp // Is it a Timestamp?
  ? T | null // Make it nullable
  : T extends object // Recursive case: Is it an object?
    ? { [K in keyof T]: WithNullableTimestamps<T[K]> } // Recurse on each property
    : T; // Fallback: Keep the type as is

/**
 * Converts a Firestore timestamp to a numeric timestamp (in milliseconds).
 */
export function serializeTimestamp<T extends ITimestamp>(value?: T): number | undefined;

/**
 * Returns the same value that was provided without any transformations.
 */
export function serializeTimestamp<T = any>(value?: T): T | undefined;

/**
 * Converts a Firestore timestamp to a numeric timestamp (in milliseconds).
 */
export function serializeTimestamp(value?: any) {
  return typeof value?.toMillis === 'function' ? value.toMillis() : value;
}

/**
 * Converts an object containing Firestore timestamps to a numeric timestamps (in milliseconds).
 */
export function serializeTimestamps<T = Record<string, unknown>>(
  data: T,
): TTimestampedSerialized<T> {
  return mapValues(
    data as Record<PropertyKey, any>,
    serializeTimestamp,
  ) as TTimestampedSerialized<T>;
}

/**
 * Converts an array of objects containing Firestore timestamps to a numeric timestamps (in
 * milliseconds).
 */
export function mapSerializeTimestamps<T = Record<string, unknown>>(
  data: T[],
): TTimestampedSerialized<T>[];

/**
 * Converts an object of objects containing Firestore timestamps to a numeric timestamps (in
 * milliseconds).
 */
export function mapSerializeTimestamps<T = Record<string, unknown>>(
  data: T,
): { [K in keyof T]: TTimestampedSerialized<T[K]> };

/**
 * Converts an array/object of objects containing Firestore timestamps to a numeric timestamps (in
 * milliseconds).
 */
export function mapSerializeTimestamps(data: any) {
  return Array.isArray(data) ? data.map(serializeTimestamps) : mapValues(data, serializeTimestamps);
}

/**
 * Converts a Firestore timestamp to a numeric timestamp (in milliseconds) recursively.
 */
export function recursiveSerializeTimestamps<T = Record<string, unknown>>(
  value: T,
): TTimestampedSerializedRecursive<T> {
  if (typeof (value as unknown as ITimestamp)?.toMillis === 'function') {
    return (value as unknown as ITimestamp).toMillis() as TTimestampedSerializedRecursive<T>;
  }

  if (isObject(value)) {
    return mapValues(
      value as Record<PropertyKey, any>,
      recursiveSerializeTimestamps,
    ) as TTimestampedSerializedRecursive<T>;
  }

  return value as TTimestampedSerializedRecursive<T>;
}
